import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LoroDoc } from "loro-crdt";
import { setProp } from "../core/ops";
import type { Task } from "../core/schema";
import { apiCall } from "../core/api";
import { Button, Input, Why } from "../ui";
import { isMeta } from "./metaPlace";

interface Lookup {
  query?: string;
  at?: string;
  count?: number;
  errors?: string[];
  error?: string;
}

/**
 * A meta place on a task: a kind of place (any pharmacy) instead of one place. The server looks the places up in
 * the person's search areas when it is set; this shows the text, how many were found (the list behind "why?"),
 * and looks them up again on request.
 */
export function MetaPlace({
  task,
  workspaceId,
  ro,
  write,
}: {
  task: Task;
  workspaceId: string;
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t, i18n } = useTranslation();
  const value = isMeta(task.props.atPlace) ? task.props.atPlace : "";
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const lookup = (task.props["atPlace.lookup"] ?? null) as Lookup | null;
  const points = (Array.isArray(task.props["atPlace.points"])
    ? task.props["atPlace.points"]
    : []) as unknown as [number, number, string][];
  const current = lookup && lookup.query === value;
  const commit = () => {
    if (draft === null) return;
    const v = draft.trim();
    setDraft(null);
    if (!v || v === value) return;
    write((doc) => setProp(doc, task.id, "atPlace", v.toLowerCase().startsWith("meta:") ? v : `meta:${v}`));
  };
  const again = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await apiCall(workspaceId, "POST", `/tasks/${encodeURIComponent(task.id)}/place/lookup`);
    } catch (e) {
      setFailed((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const when = lookup?.at
    ? new Date(lookup.at).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" })
    : "";
  return (
    <div className="flex flex-col gap-1" data-testid="meta-place">
      <Input
        compact
        disabled={ro}
        value={draft ?? value}
        placeholder={t("metaPlaces.metaPlaceholder")}
        aria-label={t("metaPlaces.metaLabel")}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") setDraft(null);
        }}
        data-testid="meta-place-input"
      />
      {value && (
        <p
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-neutral-500"
          data-testid="meta-place-status"
        >
          {!current ? (
            <span>{t("metaPlaces.lookingUp")}</span>
          ) : lookup?.error ? (
            <span className="text-warn">
              {t(`errors.${lookup.error}`, { defaultValue: t("error.generic") })}
            </span>
          ) : (
            <span>{t("metaPlaces.found", { n: lookup?.count ?? 0, when })}</span>
          )}
          {points.length > 0 && (
            <Why
              title={t("metaPlaces.foundWhy")}
              rows={points
                .slice(0, 20)
                .map(([lat, lon, name], i) => [
                  `${i + 1}. ${name}`,
                  `${Number(lat).toFixed(4)}, ${Number(lon).toFixed(4)}`,
                ])}
              formula={t("metaPlaces.foundFormula", { n: points.length })}
            />
          )}
          {!ro && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void again()}
              data-testid="meta-place-again"
            >
              {t("metaPlaces.lookAgain")}
            </Button>
          )}
          {failed && (
            <span className="text-warn">{t(`errors.${failed}`, { defaultValue: t("error.generic") })}</span>
          )}
        </p>
      )}
    </div>
  );
}
